import {
  AfterContentInit,
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil, tap } from 'rxjs/operators';

import { IACOption } from './nv-textbox.interface';

//
// https://stackoverflow.com/questions/34948961/angular-2-custom-form-input/34998780#34998780
//

@Component({
  selector: 'nv-textbox',
  template: require('./nv-textbox.component.html'),
  styles: [require('./nv-textbox.scss')],
  encapsulation: ViewEncapsulation.None,
})
export class NvTextboxComponent implements OnInit, AfterViewInit, AfterContentInit {
  /**
   *
   * The FormControl defining the text content.
   *
   * Add [Validators](https://angular.io/guide/form-validation) to the FormControl to limit/restrict input
   * @required
   */
  @Input() textControl: FormControl;

  /**
   *
   * The name of the left icon (as `nv-icon`)
   */
  @Input() icon: string = null;

  /**
   *
   * Determines whether to show the clear button
   */
  @Input() hasClearBtn: boolean = false;

  /**
   *
   * The placeholder text
   */
  @Input() placeholder: string = 'Search...';

  /**
   *
   * Determines whether to highlight invalid inputs as red.
   *
   * Use [Validators](https://angular.io/guide/form-validation) to define what inputs are valid.
   */
  @Input() showInvalid: boolean = false;

  /**
   *
   * Determines whether to highlight invalid inputs as red on a pristine(untouched) required input.
   *
   * Use with Angular built-in validators [Validators.required](https://angular.io/api/forms/Validators#required).
   */
  @Input() showInvalidOnPristine: boolean = true;

  /**
   *
   * Restricts the input to certain values
   */
  @Input() restrictToOptions: boolean = false;

  /**
   *
   * The array of autocomplete options
   * ~~~~js
   * interface IACOption {
   *  key: string;
   *  human: string;
   *  tags: {
   *    key: string;
   *    human: string;
   *  }
   * }
   * ~~~~
   */
  @Input() autocompleteOptions: IACOption[];

  /**
   *
   * The string to display when no autocomplete options are available
   */
  @Input() emptyStateText: string = 'No options found';

  /**
   *
   * The string to display to indicate that autocomplete options
   * have been trimmed due to the length of the list. This displays
   * if a value is provided, and is handled by the parent component
   */
  @Input() moreOptionsText: string;

  /**
   *
   * Temporary prop to return either a IACOption or string
   *
   * When refactoring, this should be always true
   */
  @Input() returnObject: boolean = false; // TODO -  REMOVE THIS ON REFACTOR - AT

  /**
   *
   * Emits when the `clear` button is pressed.
   *
   * The parent component must handle clearing the FormControl
   *
   */
  @Output() clearValue: EventEmitter<void> = new EventEmitter<void>();

  //
  /**
   *
   * Emits a string or IACOption only when an element is selected
   *
   * Note: currenly fires when selecting text. TODO - rename this event to remove collision with native event
   */
  @Output() selectOption: EventEmitter<IACOption | string> = new EventEmitter<IACOption | string>();

  @ViewChild('input', { static: true }) inputElement: ElementRef;
  @ViewChild(MatAutocompleteTrigger, { static: true }) autocompleteTrigger: MatAutocompleteTrigger;

  // TODO make functions more pure?
  hasAutocomplete: boolean = false;
  hasTags: boolean = false;
  showClearBtn: boolean = false;

  // valid: boolean = true;
  private debounce_time: number = 50; // millis
  private destroy$: Subject<boolean> = new Subject<boolean>();

  get showEmptyState(): boolean {
    return (
      this.autocompleteOptions &&
      this.autocompleteOptions.length === 0 &&
      !(this.emptyStateText === '' || this.emptyStateText === undefined)
    );
  }

  get showAction(): boolean {
    if (this.autocompleteOptions) {
      const matchFound = this.isExactMatch(this.textControl.value, this.autocompleteOptions);
      const searchEntered = this.textControl.value;
      return (!matchFound && searchEntered) || this.autocompleteOptions.length === 0;
    } else return false;
  }

  get isInvalid(): boolean {
    return this.showInvalid
      ? this.showInvalidOnPristine === undefined || this.showInvalidOnPristine === true
        ? !this.textControl.valid
        : !this.textControl.valid && !this.textControl.pristine
      : false;
  }

  // TODO: Figure out how to efficiently do the highlighting

  ngOnInit(): void {
    // Determine if there are autocomplete options
    if (this.autocompleteOptions && this.autocompleteOptions.length >= 0) {
      this.hasAutocomplete = true;
      // Determine if the options have tags
      this.hasTags = this.autocompleteOptions.some(opt => {
        return opt.tags && opt.tags.length > 0;
      });
    }

    // Set up the subscription to Value Changes
    this.textControl.valueChanges
      .pipe(
        debounceTime(this.debounce_time),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
        tap(value => this.onValueChange(value)),
      )
      .subscribe(null);

    // For a passed in textControl with a non-empty value
    // we also want to show the clear btn (JCHU)
    this.showClearBtn = !this.textControl.disabled && !!this.textControl.value;
  }

  ngAfterViewInit() {
    if (this.hasAutocomplete) {
      // set up subscription to autocomplete
      this.autocompleteTrigger.autocomplete.optionSelected.subscribe((event: MatAutocompleteSelectedEvent) => {
        this.onSelectOption(event);
      });

      // Restrict textbox to options
      if (this.restrictToOptions === true) {
        // New event when user clicks away from textbox (closes the AC menu)
        this.autocompleteTrigger.panelClosingActions.subscribe(event => {
          if (!event || !event.source) {
            this.onClosePanel(this.textControl.value, this.autocompleteOptions);
          }
        });
      }
    }
  }

  ngAfterContentInit() {
    // Disable autocomplete if necessary
    if (this.hasAutocomplete) {
      this.autocompleteTrigger.autocompleteDisabled = false;
    } else {
      this.autocompleteTrigger.autocompleteDisabled = true;
    }
  }

  // Subscription handlers
  onValueChange(text_value: string): void {
    if (!!text_value && text_value.length > 0) {
      this.showClearBtn = true;
    } else {
      this.showClearBtn = false;
    }
  }

  onSelectOption(event: MatAutocompleteSelectedEvent): void {
    // TODO - this should be attached to a flag
    event.option.deselect(); // deselect the element from the dropdown
    this.emitSelectEvent(event.option.value);
    this.inputElement.nativeElement.blur(); // Unfocus the textbox
  }

  onClosePanel(current_value: string, options: IACOption[]): void {
    let selectedOption: IACOption;
    if (!!current_value && !!options) {
      // Is the current text a valid option?
      selectedOption = options.find(opt => opt.human.toLowerCase() === current_value.toLowerCase());
    }
    if (!!selectedOption) {
      // If the current text matches the `human` text of an option, select that option
      this.emitSelectEvent(selectedOption);
    } else {
      this.clearValue.emit(); // Reset the textbox when closing the menu
    }
  }

  emitSelectEvent(option: IACOption): void {
    if (this.returnObject) {
      this.selectOption.emit(option); // Emit the select event with the selected object
    } else {
      this.selectOption.emit(option.human); // Emit the select event with the string // TODO - REMOVE THIS ON REFACTOR - AT
    }
  }

  isExactMatch(text: string, options: IACOption[]): boolean {
    let foundIndex = -1;
    if (!!options && !!text) {
      foundIndex = options.findIndex(opt => {
        // trim leading and trailing whitespace to remove accidental duplicates (JE)
        return opt.human.toLowerCase() === text.toLowerCase().trim();
      });
    }
    return foundIndex >= 0;
  }

  // Used in template on icon-button press
  // tslint:disable-next-line:no-unused-variable
  clearSearch(): void {
    this.clearValue.emit();
    this.inputElement.nativeElement.focus();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
